import { QueryFunctionContext } from '@tanstack/react-query';

import { ApiError } from './error';

/**
 * API Bridge Utilities for TanStack Query Integration
 *
 * This module provides utilities to bridge the gap between our existing API connector
 * and TanStack Query's expected behavior. Our API connector returns `T | ApiError`
 * (doesn't throw), but TanStack Query expects promises that throw on error.
 *
 * These utilities handle the conversion and provide type-safe error handling.
 */

/**
 * Type guard to check if a response is an ApiError instance
 *
 * @param response - The response to check
 * @returns True if the response is an ApiError, false otherwise
 */
export function isApiError(response: unknown): response is ApiError {
    return response instanceof ApiError;
}

/**
 * Unwraps API response or throws ApiError for TanStack Query compatibility
 *
 * Our API connector returns `T | ApiError`, but TanStack Query expects
 * promises that throw on error. This function bridges the gap by:
 * - Returning the data if successful
 * - Throwing the ApiError if it's an error response
 *
 * @param response - The API response (either data or ApiError)
 * @returns The unwrapped data
 * @throws ApiError if the response is an error
 */
export function unwrapApiResponse<T>(response: T | ApiError): T {
    if (isApiError(response)) {
        throw response;
    }

    return response;
}

/**
 * Creates a query function that wraps GET requests with error handling
 *
 * This function takes an API call that returns `T | ApiError` and wraps it
 * to work with TanStack Query's `useQuery` hook. It:
 * - Forwards the AbortSignal for request cancellation
 * - Converts ApiError responses to thrown errors
 * - Returns the unwrapped data on success
 *
 * @param apiCall - The API function to wrap
 * @returns A TanStack Query-compatible query function
 *
 * @example
 * ```typescript
 * const queryFetchData = makeGetQueryFn(
 *   ({ signal }) => api.get('/endpoint', { signal })
 * );
 *
 * // Use with useQuery
 * const { data, error } = useQuery({
 *   queryKey: ['data'],
 *   queryFn: queryFetchData
 * });
 * ```
 */
export function makeGetQueryFn<T>(
    apiCall: (config?: { signal?: AbortSignal }) => Promise<T | ApiError>
) {
    return async ({ signal }: QueryFunctionContext): Promise<T> => {
        const response = await apiCall({ signal });

        return unwrapApiResponse(response);
    };
}

/**
 * Creates a mutation function that wraps POST/PUT requests with error handling
 *
 * This function takes an API call that returns `TResponse | ApiError` and wraps it
 * to work with TanStack Query's `useMutation` hook. It:
 * - Converts ApiError responses to thrown errors
 * - Returns the unwrapped response on success
 *
 * @param apiCall - The API function to wrap
 * @returns A TanStack Query-compatible mutation function
 *
 * @example
 * ```typescript
 * const mutationUpdateData = makeMutationFn(
 *   (payload: UpdatePayload) => api.post('/endpoint', payload)
 * );
 *
 * // Use with useMutation
 * const { mutate, error } = useMutation({
 *   mutationFn: mutationUpdateData
 * });
 * ```
 */
export function makeMutationFn<TPayload, TResponse>(
    apiCall: (payload: TPayload) => Promise<TResponse | ApiError>
) {
    return async (payload: TPayload): Promise<TResponse> => {
        const response = await apiCall(payload);

        return unwrapApiResponse(response);
    };
}

/**
 * Helper to map ApiError to user-friendly message with i18n support
 *
 * This function extracts a user-friendly error message from various error types,
 * with support for i18n translations based on error codes and context.
 *
 * @param error - The error to map
 * @param context - Optional context for more specific error messages (e.g., 'orders', 'candidates')
 * @param t - Optional translation function for i18n support
 * @returns A user-friendly error message
 *
 * @example
 * ```typescript
 * const { error } = useQuery({ queryKey: ['data'], queryFn: fetchData });
 * const { t } = useTranslation('errors');
 *
 * if (error) {
 *   const message = mapApiErrorToMessage(error, 'orders', t);
 *   showToast(message, 'error');
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Assumes error keys are structured like:
 * // {
 * //   "100001_orders": "Order not found. Please try again.",
 * //   "100001": "The item was not found.",
 * // }
 *
 * // With context 'orders' and i18n
 * const message = mapApiErrorToMessage(apiErrorWithCode100001, 'orders', t);
 * // Returns: "Order not found. Please try again."
 *
 * // Without context but with i18n
 * const message = mapApiErrorToMessage(apiErrorWithCode100001, undefined, t);
 * // Returns: "The item was not found."
 * ```
 */
export function mapApiErrorToMessage(
    error: unknown,
    context?: string,
    t?: (keys: string | string[], options?: { defaultValue: string }) => string
): string {
    if (isApiError(error)) {
        // If a translation function is provided, try to find a translation
        if (t) {
            const fallbackMessage = error.message || 'An error occurred';
            const keysToTry: string[] = [];

            // 1. First, try the most specific key with context, e.g., "100001_orders"
            if (context) {
                keysToTry.push(`${ error.errorCode }_${ context }`);
            }

            // 2. Fall back to the generic error code key, e.g., "100001"
            keysToTry.push(error.errorCode);

            // i18next's `t` function can take an array of keys. It will try them in order
            // and return the first one that has a valid translation. If none are found,
            // it will use the provided defaultValue.
            return t(keysToTry, { defaultValue: fallbackMessage });
        }

        // If no translation function is provided, fall back to the original error message
        return error.message || 'An error occurred';
    }

    if (error instanceof Error) {
        return error.message;
    }

    return 'An unknown error occurred';
}
